/**
 * Google Consent Mode integration (gtag).
 *
 * This plugin does not emit ProsPix events; it mirrors ProsPix consent into
 * Google consent signals when `window.gtag` is present.
 */
import type { PluginContext } from "../lib/types";

import { registerPlugin } from "./register";

type GoogleConsentState = "granted" | "denied";

function toGoogleConsentState(value: boolean): GoogleConsentState {
  return value ? "granted" : "denied";
}

registerPlugin("gcm", (ctx: PluginContext) => {
  const getGtag = () => {
    const gtag = (window as any)?.gtag;
    return typeof gtag === "function" ? gtag : undefined;
  };

  const buildConsent = () => {
    const analyticsAllowed = ctx.canTrack("analytics");
    const marketingAllowed = ctx.canTrack("marketing");
    return {
      analytics_storage: toGoogleConsentState(analyticsAllowed),
      ad_storage: toGoogleConsentState(marketingAllowed),
      ad_user_data: toGoogleConsentState(marketingAllowed),
      ad_personalization: toGoogleConsentState(marketingAllowed),
    } as const;
  };

  const applyConsent = (mode: "default" | "update") => {
    const gtag = getGtag();
    if (!gtag) return false;
    try {
      gtag("consent", mode, buildConsent());
      return true;
    } catch {
      return false;
    }
  };

  let pollTimer: ReturnType<typeof setInterval> | undefined;
  let unsubscribe: (() => void) | undefined;

  const ensureApplied = () => {
    if (applyConsent("default")) return;
    if (pollTimer) return;
    pollTimer = setInterval(() => {
      if (applyConsent("default")) {
        if (pollTimer) clearInterval(pollTimer);
        pollTimer = undefined;
      }
    }, 1000);
  };

  return {
    onInit() {
      ensureApplied();
      unsubscribe = ctx.on("consent", () => {
        applyConsent("update");
      });
    },
    onUnload() {
      if (unsubscribe) unsubscribe();
      if (pollTimer) clearInterval(pollTimer);
    },
  };
});
